/**
 * CheckboxQuestion Component
 * Allows multiple selections from a list of options
 */

import { QuestionOption } from '../../../types/formConfig'

interface CheckboxQuestionProps {
  questionId: string
  options: QuestionOption[]
  value: string[]
  onChange: (value: string[]) => void
  required?: boolean
}

const CheckboxQuestion = ({
  questionId,
  options,
  value = [],
  onChange,
  required = false
}: CheckboxQuestionProps) => {
  const handleToggle = (optionValue: string) => {
    const currentValues = Array.isArray(value) ? value : []

    if (currentValues.includes(optionValue)) {
      // Remove the value
      onChange(currentValues.filter(v => v !== optionValue))
    } else {
      // Add the value
      onChange([...currentValues, optionValue])
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent, optionValue: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleToggle(optionValue)
    }
  }

  return (
    <div className="w-full max-w-2xl">
      <div className="space-y-3">
        {options.map((option) => {
          const isChecked = Array.isArray(value) && value.includes(option.value)

          return (
            <div
              key={option.value}
              onClick={() => handleToggle(option.value)}
              onKeyDown={(e) => handleKeyDown(e, option.value)}
              role="checkbox"
              aria-checked={isChecked}
              aria-required={required}
              tabIndex={0}
              className={`
                relative cursor-pointer rounded-lg border-2 p-4 transition-all duration-200
                ${
                  isChecked
                    ? 'border-primary-green bg-primary-green bg-opacity-5'
                    : 'border-gray-200 bg-white hover:border-primary-green'
                }
                focus:outline-none focus:ring-2 focus:ring-primary-green focus:ring-opacity-50
              `}
            >
              <div className="flex items-start gap-4">
                {/* Checkbox */}
                <div className="flex-shrink-0 pt-1">
                  <div
                    className={`
                      w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200
                      ${
                        isChecked
                          ? 'bg-primary-green border-primary-green'
                          : 'bg-white border-gray-300'
                      }
                    `}
                  >
                    {isChecked && (
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="3"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-grow">
                  <h3
                    className={`font-semibold mb-1 ${
                      isChecked ? 'text-primary-green' : 'text-dark-plum'
                    }`}
                  >
                    {option.label}
                  </h3>

                  {option.description && (
                    <p className="text-sm text-gray-600">
                      {option.description}
                    </p>
                  )}
                </div>

                {/* Hidden checkbox input for form compatibility */}
                <input
                  type="checkbox"
                  name={questionId}
                  value={option.value}
                  checked={isChecked}
                  onChange={() => handleToggle(option.value)}
                  className="sr-only"
                  aria-hidden="true"
                  tabIndex={-1}
                />
              </div>
            </div>
          )
        })}
      </div>

      {/* Selected count */}
      {value.length > 0 && (
        <div className="mt-4 text-sm text-gray-600">
          {value.length} option{value.length !== 1 ? 's' : ''} selected
        </div>
      )}
    </div>
  )
}

export default CheckboxQuestion
